import { Component, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import {
  AlertController,
  LoadingController,
  NavController,
  NavParams,
  ToastController,
  ViewController,
} from "ionic-angular";
import { Action, CanceledAction } from "models/order-management/models";
import { StatefulOrderResponse } from "models/order-management/statefullOrderResponse";
import CompanyResourceSummary from "models/resource-management/company-resource-summary";
import * as moment from "moment-timezone";
import { OrderManagementDataService } from "providers/order-management-data-service/order-management-data-service";
import { take } from "rxjs/operators";
import { Subject } from "rxjs/Subject";
import { OrderPatchRequest } from "../../../models/order-management/orderPatchRequest";

export interface VoidLOCOptions {
  discontinue: boolean;
  openAddOrderModal: boolean;
  userCanceledOutOfModal: boolean;
}

@Component({
  selector: "cancel-order",
  templateUrl: "cancel-order.html",
})
export default class CancelOrder implements OnDestroy {
  voidForm: FormGroup;
  providers: CompanyResourceSummary[] = [];
  providerSearchSubject: Subject<string> = new Subject();
  spinner: any = null;
  spinnerCounter: number = 0;
  patientChartId: number = 0;
  order: StatefulOrderResponse;
  userDisplayName: string;
  showWarningToAddNewLOCOrder = false;
  lastLOCOrderId = 0;

  public constructor(
    private fb: FormBuilder,
    public navCtrl: NavController,
    private navParams: NavParams,
    private alertController: AlertController,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private orderManagementDataService: OrderManagementDataService,
    private viewCtrl: ViewController
  ) {
    this.order = this.navParams.data.order;
    this.userDisplayName = this.navParams.data.userDisplayName;
    this.lastLOCOrderId = this.navParams.data.lastLOCOrderId;
    const voidDate = new Date(this.order.cancelDate);
    voidDate.setHours(0, 0, 0, 0);
    this.voidForm = new FormGroup({
      voidDate: this.fb.control(
        this.order.cancelDate ? voidDate.toString() : new Date().toDateString(),
        Validators.required
      ),
      voidReason: this.fb.control("", Validators.required),
    });
    if (this.lastLOCOrderId > 0) {
      this.showWarningToAddNewLOCOrder = true;
    }
  }

  ngOnDestroy(): void {}
  get voidDate(): string {
    const selectedDate = this.voidForm.get("voidDate").value;
    if (selectedDate) {
      const currentDate = moment().startOf("day");
      const selectedDateTime = moment(selectedDate);

      if (
        selectedDateTime.isSame(currentDate) ||
        selectedDateTime.isBefore(currentDate)
      ) {
        return selectedDateTime.startOf("day").toISOString();
      } else {
        return selectedDateTime.endOf("day").toISOString();
      }
    }
    return null; // Return null if no date is selected
  }

  get voidReason(): string {
    return this.voidForm.get("voidReason").value;
  }

  save(): void {
    const paramData: Partial<VoidLOCOptions> = {
      discontinue: true,
      openAddOrderModal: true,
      userCanceledOutOfModal: false,
    };
    let voidAction: CanceledAction = {
      actionType: Action.ActionTypeEnum.Canceled,
      reason: this.voidReason,
      displayName: this.userDisplayName,
      timezone: moment.tz.guess(),
    };

    if (this.lastLOCOrderId > 0) {
      this.showWarningToAddNewLOCOrder = true;
    }

    let discontinuePath: OrderPatchRequest = {
      ...voidAction,
    };
    this.increaseSpinnerCount("Saving ...");
    this.orderManagementDataService
      .updateOrder(this.order.orderId, discontinuePath)
      .pipe(take(1))
      .subscribe(
        (x) => {
          this.decreaseSpinnerCount();
          this.viewCtrl.dismiss(paramData);
        },
        (err) => {
          console.error("Error saving");
          console.error(err);

          this.decreaseSpinnerCount();
          this.presentToast("Error canceling medication");
        }
      );
  }

  cancel(): void {
    const paramData: Partial<VoidLOCOptions> = {
      discontinue: true,
      openAddOrderModal: false,
      userCanceledOutOfModal: true,
    };
    /**
     * Handles the cancel event
     */
    let alert = this.alertController.create({
      title: "Cancel Confirmation",
      message: "Are you sure you wish to cancel",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            this.viewCtrl.dismiss(paramData);
          },
        },
      ],
    });
    alert.present();
  }

  presentToast(message) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 3000,
      position: "below",
      cssClass: "error",
    });

    toast.onDidDismiss(() => {});

    toast.present();
  }

  increaseSpinnerCount(message) {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/orders-entry-om/cancel-order/cancel-order.ts